import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  createColumnHelper,
  ColumnDef,
  Row,
  SortingState,
} from '@tanstack/react-table';
import {
  Tag,
  InlineLoading,
  Pagination,
  Button,
  Link as CarbonLink,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import { format2 } from 'utils/number';
import {
  foucsDiagnosisLoadableAtom,
  foucsDiagnosisPageAtom,
  foucsDiagnosisPageSizeAtom,
  foucsDiagnosisSortAtom,
  foucsDiagnosisAnalysisIdAtom,
  checkIntersectionsAtomFamily,
  recalculateAtomFamily,
  runCoverageAnalysisLoadableAtom,
  coverageResultsAtom,
} from './atoms';
import type {
  FoucsDiagnosisRecord,
  RawIntersection,
  FoucsSelectedUnit,
  FailureReason,
  CoverageAnalysisResponse,
  CoverageAnalysisResultsResponse,
  CoverageAnalysisStatus,
  CoverageEntry,
} from './types';
import styles from './page.module.scss';

const FAILURE_REASON_CONFIG: Record<
  FailureReason,
  { label: string; tagType: string }
> = {
  all_filtered: {
    label: 'No Eligible Horizontal Units',
    tagType: 'magenta',
  },
  overlap_conflicts: { label: 'Overlap Conflicts', tagType: 'teal' },
  should_match: { label: 'Should Match', tagType: 'green' },
  below_threshold: { label: 'Below Threshold', tagType: 'red' },
  needs_analysis: { label: 'Needs Analysis', tagType: 'purple' },
};

const columnHelper = createColumnHelper<FoucsDiagnosisRecord>();

const intersectionColumnHelper = createColumnHelper<RawIntersection>();

const foucsColumnHelper = createColumnHelper<FoucsSelectedUnit>();

const columns = [
  columnHelper.accessor('subtractName', {
    id: 'subtractName',
    header: 'Subtract Name',
    size: 250,
  }),
  columnHelper.accessor('calculationTimestamp', {
    id: 'calculationTimestamp',
    header: 'Calculation Timestamp',
    size: 220,
    meta: { type: 'datetime' },
  }),
  columnHelper.accessor('failureReason', {
    id: 'failureReason',
    header: 'Failure Reason',
    size: 170,
    cell: (info) => {
      const reason = info.getValue();
      if (!reason) return '--';
      const config = FAILURE_REASON_CONFIG[reason];
      return (
        <Tag type={config.tagType as never} size="sm">
          {config.label}
        </Tag>
      );
    },
  }),
  columnHelper.display({
    id: 'customConfig',
    header: 'Custom Config',
    size: 180,
    cell: (info) => {
      const row = info.row.original;
      const parts: string[] = [];
      if (row.coverageThreshold !== 0.01)
        parts.push(`Cov: ${format2(row.coverageThreshold * 100)}%`);
      if (row.overlapThreshold !== 0.01)
        parts.push(`Ovlp: ${format2(row.overlapThreshold * 100)}%`);
      if (row.minDaCoverage !== 0.99)
        parts.push(`Min: ${format2(row.minDaCoverage * 100)}%`);
      if (parts.length === 0) return '--';
      return (
        <Tag type="blue" size="sm">
          {parts.join(', ')}
        </Tag>
      );
    },
  }),
];

const intersectionColumns: ColumnDef<RawIntersection>[] = [
  intersectionColumnHelper.accessor('targetName', {
    id: 'targetName',
    header: 'Name',
    size: 220,
    cell: (info) => {
      const row = info.row.original;
      return (
        <CarbonLink
          href={`/development_areas/${row.targetId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {info.getValue()}
        </CarbonLink>
      );
    },
  }) as ColumnDef<RawIntersection>,
  intersectionColumnHelper.accessor('unitDevelopmentType', {
    id: 'unitDevelopmentType',
    header: 'Type',
    size: 100,
    cell: (info) => {
      const value = info.getValue();
      return (
        <Tag type={value === 'horizontal' ? 'green' : 'gray'} size="sm">
          {value}
        </Tag>
      );
    },
  }) as ColumnDef<RawIntersection>,
  intersectionColumnHelper.accessor('sourceCoverageRatio', {
    id: 'sourceCoverageRatio',
    header: 'Source Coverage %',
    size: 150,
    cell: (info) => format2(info.getValue() * 100) + '%',
  }) as ColumnDef<RawIntersection>,
  intersectionColumnHelper.accessor('acreage', {
    id: 'acreage',
    header: 'Acreage',
    size: 120,
    cell: (info) => {
      const val = info.getValue();
      return val !== null && val !== undefined ? format2(val) : '--';
    },
  }) as ColumnDef<RawIntersection>,
  intersectionColumnHelper.accessor('ltdCapable', {
    id: 'ltdCapable',
    header: 'LTD Capable',
    size: 120,
    cell: (info) => {
      const value = info.getValue();
      return (
        <Tag type={value ? 'green' : 'red'} size="sm">
          {value ? 'Yes' : 'No'}
        </Tag>
      );
    },
  }) as ColumnDef<RawIntersection>,
  intersectionColumnHelper.accessor('ltdOverlaps', {
    id: 'ltdOverlaps',
    header: 'Overlap w/ LTD Capable',
    size: 400,
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '--';
      // Parse each "UnitName (X%)" entry and color red if over 1% (default overlap threshold)
      const parts = value.split(', ');
      return (
        <span
          title={value}
          style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {parts.map((part, i) => {
            const match = part.match(/\(([0-9.]+)%\)/);
            const pct = match ? parseFloat(match[1]) : 0;
            return (
              <span key={i}>
                {i > 0 && ', '}
                <span className={pct > 1 ? styles.belowThreshold : undefined}>
                  {part}
                </span>
              </span>
            );
          })}
        </span>
      );
    },
  }) as ColumnDef<RawIntersection>,
];

const foucsColumns: ColumnDef<FoucsSelectedUnit>[] = [
  foucsColumnHelper.accessor('unitName', {
    id: 'unitName',
    header: 'Unit Name',
    size: 220,
    cell: (info) => {
      const row = info.row.original;
      return (
        <CarbonLink
          href={`/development_areas/${row.unitId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {info.getValue()}
        </CarbonLink>
      );
    },
  }) as ColumnDef<FoucsSelectedUnit>,
  foucsColumnHelper.accessor('percentCoverage', {
    id: 'percentCoverage',
    header: 'Coverage %',
    size: 150,
    cell: (info) => format2(info.getValue() * 100) + '%',
  }) as ColumnDef<FoucsSelectedUnit>,
  foucsColumnHelper.accessor('acreage', {
    id: 'acreage',
    header: 'Acreage',
    size: 120,
    cell: (info) => {
      const val = info.getValue();
      return val !== null && val !== undefined ? format2(val) : '--';
    },
  }) as ColumnDef<FoucsSelectedUnit>,
];

const foucsTableConfig = {
  sorting: false,
  globalSearch: false,
  columnFilters: true,
  columnReordering: false,
  columnPinning: false,
  columnVisibility: false,
  export: false,
};

const DiagnosisSubRow: FC<{ scarId: number }> = ({ scarId }) => {
  const [state] = useAtom(checkIntersectionsAtomFamily(scarId));
  const trigger = useSetAtom(checkIntersectionsAtomFamily(scarId));

  useEffect(() => {
    if (state.status === 'idle') {
      trigger('fetch');
    }
  }, [state.status, trigger]);

  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <div className={styles.subrowContent}>
        <InlineLoading description="Running diagnosis..." />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className={styles.subrowContent}>
        <div className={styles.error}>Error: {state.error}</div>
      </div>
    );
  }

  if (state.status !== 'loaded') {
    return null;
  }

  const { data } = state;

  if (data.error) {
    return (
      <div className={styles.subrowContent}>
        <div className={styles.error}>{data.error}</div>
      </div>
    );
  }

  const eligibleHorizontalCoverage = data.rawIntersections
    .filter((r) => r.unitDevelopmentType === 'horizontal' && r.ltdCapable)
    .reduce((sum, r) => sum + r.sourceCoverageRatio, 0);

  return (
    <div className={styles.subrowContent}>
      {data.parameters && (
        <div className={styles.parametersBar}>
          <span>
            Min DA Coverage:{' '}
            {format2(data.parameters.minimumDevelopmentAreaCoverage * 100)}%
          </span>
          <span>
            Overlap Threshold: {format2(data.parameters.overlapThreshold * 100)}
            %
          </span>
          <span>
            Coverage Threshold:{' '}
            {format2(data.parameters.coverageThreshold * 100)}%
          </span>
          <span className={styles.coverageSummary}>
            Eligible Horizontal Coverage:{' '}
            <strong
              className={
                data.parameters &&
                eligibleHorizontalCoverage <
                  data.parameters.minimumDevelopmentAreaCoverage
                  ? styles.belowThreshold
                  : styles.aboveThreshold
              }
            >
              {format2(eligibleHorizontalCoverage * 100)}%
            </strong>{' '}
            (LTD Capable only)
          </span>
        </div>
      )}

      <div className={styles.diagnosisSection}>
        <div className={styles.sectionTitle}>
          Raw Intersections ({data.rawIntersections.length})
        </div>
        <div className={styles.sectionHelp}>
          All development areas that spatially intersect this subtract.{' '}
          <strong>Source Coverage %</strong> is how much of the subtract each
          unit covers. Only <strong>LTD Capable</strong> units are eligible for
          FOUCS — non-LTD units do not count toward horizontal coverage.
        </div>
        {data.rawIntersections.length > 0 ? (
          <DataTable
            data={[...data.rawIntersections].sort((a, b) => {
              // Horizontal first, then LTD Capable, then coverage desc
              const typeOrder =
                (a.unitDevelopmentType === 'horizontal' ? 0 : 1) -
                (b.unitDevelopmentType === 'horizontal' ? 0 : 1);
              if (typeOrder !== 0) return typeOrder;
              const ltdOrder = (a.ltdCapable ? 0 : 1) - (b.ltdCapable ? 0 : 1);
              if (ltdOrder !== 0) return ltdOrder;
              return b.sourceCoverageRatio - a.sourceCoverageRatio;
            })}
            columns={intersectionColumns}
            getRowId={(row) => String(row.targetId)}
            persistenceKey={`foucs-diagnosis-intersections-${scarId}`}
            config={{
              sorting: true,
              globalSearch: false,
              columnFilters: true,
              columnReordering: false,
              columnPinning: false,
              columnVisibility: false,
              export: false,
            }}
          />
        ) : (
          <div className={styles.noResults}>
            No intersecting development areas found
          </div>
        )}
      </div>

      <div className={styles.diagnosisSection}>
        <div className={styles.sectionTitle}>FOUCS Horizontal Result</div>
        <div className={styles.sectionHelp}>
          The optimal non-overlapping set of horizontal units selected by FOUCS.
          Empty means no valid combination could reach the minimum coverage
          threshold.
        </div>
        {data.foucsResult.horizontal.length > 0 ? (
          <DataTable
            data={data.foucsResult.horizontal}
            columns={foucsColumns}
            getRowId={(row) => String(row.unitId)}
            persistenceKey={`foucs-diagnosis-foucs-h-${scarId}`}
            config={foucsTableConfig}
          />
        ) : (
          <div className={styles.noResults}>
            No valid horizontal coverage set found
          </div>
        )}
      </div>

      <div className={styles.diagnosisSection}>
        <div className={styles.sectionTitle}>FOUCS Vertical Result</div>
        <div className={styles.sectionHelp}>
          The optimal non-overlapping set of vertical units selected by FOUCS.
        </div>
        {data.foucsResult.vertical.length > 0 ? (
          <DataTable
            data={data.foucsResult.vertical}
            columns={foucsColumns}
            getRowId={(row) => String(row.unitId)}
            persistenceKey={`foucs-diagnosis-foucs-v-${scarId}`}
            config={foucsTableConfig}
          />
        ) : (
          <div className={styles.noResults}>
            No valid vertical coverage set found
          </div>
        )}
      </div>
    </div>
  );
};

const CheckIntersectionsButton: FC<{ scarId: number }> = ({ scarId }) => {
  const [state] = useAtom(checkIntersectionsAtomFamily(scarId));
  const setDiagnosis = useSetAtom(checkIntersectionsAtomFamily(scarId));

  const handleClick = useCallback(() => {
    setDiagnosis('fetch');
  }, [setDiagnosis]);

  return (
    <Button
      size="sm"
      kind="ghost"
      onClick={handleClick}
      disabled={state.status === 'loading'}
    >
      {state.status === 'loading' ? 'Diagnosing...' : 'Check Intersections'}
    </Button>
  );
};

const RecalculateButton: FC<{ scarId: number }> = ({ scarId }) => {
  const [state] = useAtom(recalculateAtomFamily(scarId));
  const setRecalculate = useSetAtom(recalculateAtomFamily(scarId));

  const handleClick = useCallback(() => {
    setRecalculate('recalculate');
  }, [setRecalculate]);

  if (state.status === 'enqueued') {
    return (
      <Tag type="green" size="sm">
        Enqueued
      </Tag>
    );
  }

  return (
    <Button
      size="sm"
      kind="ghost"
      onClick={handleClick}
      disabled={state.status === 'loading'}
    >
      {state.status === 'loading' ? 'Enqueuing...' : 'Recalculate'}
    </Button>
  );
};

export const FoucsDiagnosisPage: FC = () => {
  const loadable = useAtomValue(foucsDiagnosisLoadableAtom);
  const [page, setPage] = useAtom(foucsDiagnosisPageAtom);
  const [, setPageSize] = useAtom(foucsDiagnosisPageSizeAtom);
  const setSort = useSetAtom(foucsDiagnosisSortAtom);
  const setAnalysisIdParam = useSetAtom(foucsDiagnosisAnalysisIdAtom);
  const [runState, triggerRun] = useAtom(runCoverageAnalysisLoadableAtom);
  const setCoverageResults = useSetAtom(coverageResultsAtom);
  const coverageResults = useAtomValue(coverageResultsAtom);

  const [analysisStatus, setAnalysisStatus] =
    useState<CoverageAnalysisStatus>('idle');
  const [analysisChannel, setAnalysisChannel] = useState<string | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<{
    processed: number;
    total: number;
  } | null>(null);
  const [analysisElapsed, setAnalysisElapsed] = useState<number | null>(null);
  const [classifyingProgress, setClassifyingProgress] = useState<{
    processed: number;
    total: number;
  } | null>(null);

  // Merge coverage results into row data and reclassify failure reasons
  const data = useMemo(() => {
    const rawData =
      loadable.state === 'hasData' ? (loadable.data?.data ?? []) : [];

    return rawData.map((row) => {
      const entry = coverageResults[row.id] ?? null;
      const coverage = entry?.eligible_coverage ?? null;
      let failureReason = row.failureReason;

      if (coverage !== null) {
        if (coverage >= 0.99 && entry?.should_match)
          failureReason = 'should_match';
        else if (coverage >= 0.99) failureReason = 'overlap_conflicts';
        else if (coverage > 0) failureReason = 'below_threshold';
        else failureReason = 'all_filtered';
      } else if (failureReason === 'all_filtered') {
        failureReason = 'needs_analysis';
      }

      return {
        ...row,
        eligibleHorizontalCoverage: coverage,
        horizontalCount: entry?.horizontal_count ?? null,
        verticalCount: entry?.vertical_count ?? null,
        failureReason,
      };
    });
  }, [loadable, coverageResults]);
  const stats = loadable.state === 'hasData' ? loadable.data?.stats : null;
  const serverBreakdown =
    loadable.state === 'hasData' ? loadable.data?.failureReasonBreakdown : null;

  // Recompute breakdown from coverage results when analysis is complete
  const hasCoverageResults = Object.keys(coverageResults).length > 0;
  const breakdown = useMemo(() => {
    if (!serverBreakdown) return null;
    if (!hasCoverageResults) {
      return {
        ...serverBreakdown,
        needsAnalysis: serverBreakdown.allFiltered,
        allFiltered: 0,
        shouldMatch: 0,
      };
    }

    // Reclassify all SCARs based on coverage analysis results
    let allFiltered = 0;
    let overlapConflicts = 0;
    let shouldMatch = 0;
    let belowThreshold = 0;

    for (const entry of Object.values(coverageResults)) {
      const coverage = entry?.eligible_coverage ?? 0;
      if (coverage >= 0.99 && entry?.should_match) shouldMatch++;
      else if (coverage >= 0.99) overlapConflicts++;
      else if (coverage > 0) belowThreshold++;
      else allFiltered++;
    }

    return {
      allFiltered,
      overlapConflicts,
      shouldMatch,
      belowThreshold,
      needsAnalysis: 0,
    };
  }, [serverBreakdown, coverageResults, hasCoverageResults]);
  const pagination =
    loadable.state === 'hasData' ? loadable.data?.pagination : null;
  const isLoading = loadable.state === 'loading';
  const error =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error
        : new Error('Failed to fetch data')
      : null;

  // Handle run response
  useEffect(() => {
    if (runState.state !== 'hasData' || !runState.data) return;
    const data = runState.data as CoverageAnalysisResponse;
    setAnalysisId(data.analysisId);
    setAnalysisChannel(data.channel);
    setAnalysisStatus('running');
    setAnalysisProgress(null);
    setAnalysisElapsed(null);
    setCoverageResults({});
  }, [runState, setCoverageResults]);

  // Normalize string keys from JSON to numeric keys for lookup by row.id.
  // Skips update if count hasn't changed to avoid no-op re-renders during polling.
  const lastStatusRef = useRef<string | null>(null);
  const lastCountRef = useRef(0);
  const applyResults = useCallback(
    (raw: Record<string | number, CoverageEntry | null>, status?: string) => {
      const entries = Object.entries(raw);
      const statusChanged = status !== lastStatusRef.current;
      if (
        !statusChanged &&
        entries.length === lastCountRef.current &&
        lastCountRef.current > 0
      )
        return;
      lastCountRef.current = entries.length;
      lastStatusRef.current = status ?? null;
      const normalized: Record<number, CoverageEntry | null> = {};
      for (const [key, value] of entries) {
        normalized[Number(key)] = value;
      }
      setCoverageResults(normalized);
    },
    [setCoverageResults]
  );

  // Fetch results from cache
  const fetchResults = useCallback(
    async (currentAnalysisId: string) => {
      const response = await request.get<CoverageAnalysisResultsResponse>(
        `/admin/foucs_diagnosis/coverage_analysis_results?analysis_id=${currentAnalysisId}`
      );
      if (!('error' in response)) {
        applyResults(response.results, response.status);
      }
    },
    [applyResults]
  );

  // Pusher subscription
  useEffect(() => {
    if (!analysisId || !analysisChannel) return;

    const subscription = subscribe(analysisChannel);

    subscription.bind(
      'batch_complete',
      (data: { processed: number; total: number }) => {
        setAnalysisProgress({ processed: data.processed, total: data.total });
        fetchResults(analysisId);
      }
    );

    subscription.bind(
      'analysis_complete',
      (data: { total: number; elapsed_seconds: number }) => {
        setAnalysisElapsed(data.elapsed_seconds);
        setAnalysisStatus('complete');
        setAnalysisIdParam(analysisId);
        fetchResults(analysisId);
      }
    );

    subscription.bind('analysis_error', () => setAnalysisStatus('error'));

    return () => {
      subscription.unbind_all?.();
    };
  }, [analysisId, analysisChannel, fetchResults, setAnalysisIdParam]);

  // Polling fallback — works even when Pusher is unavailable
  useEffect(() => {
    if (
      (analysisStatus !== 'running' && analysisStatus !== 'classifying') ||
      !analysisId
    )
      return;

    const interval = setInterval(async () => {
      const response = await request.get<CoverageAnalysisResultsResponse>(
        `/admin/foucs_diagnosis/coverage_analysis_results?analysis_id=${analysisId}`
      );
      if ('error' in response) return;

      applyResults(response.results, response.status);
      if (response.total) {
        setAnalysisProgress({
          processed: response.count,
          total: response.total,
        });
      }

      if (response.status === 'complete') {
        setAnalysisStatus('complete');
        if (response.elapsedSeconds)
          setAnalysisElapsed(response.elapsedSeconds);
        setAnalysisIdParam(analysisId);
      } else if (response.status === 'classifying') {
        setAnalysisStatus('classifying');
        if (response.classifyingTotal) {
          setClassifyingProgress({
            processed: response.classifyingProcessed ?? 0,
            total: response.classifyingTotal,
          });
        }
      } else if (response.status === 'error') {
        setAnalysisStatus('error');
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [analysisStatus, analysisId, applyResults, setAnalysisIdParam]);

  const allColumns = useMemo(
    () =>
      [
        ...columns,
        columnHelper.accessor('horizontalCount', {
          id: 'horizontalCount',
          header: 'H Units',
          size: 80,
          cell: (info) => info.getValue() ?? '--',
        }),
        columnHelper.accessor('verticalCount', {
          id: 'verticalCount',
          header: 'V Units',
          size: 80,
          cell: (info) => info.getValue() ?? '--',
        }),
        columnHelper.accessor('eligibleHorizontalCoverage', {
          id: 'eligibleHorizontalCoverage',
          header: 'Eligible Horizontal Coverage',
          size: 200,
          cell: (info) => {
            const value = info.getValue();
            if (value === null || value === undefined) return '--';
            return (
              <span
                className={
                  value < 0.99 ? styles.belowThreshold : styles.aboveThreshold
                }
              >
                {format2(value * 100)}%
              </span>
            );
          },
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          size: 280,
          cell: (info) => (
            <div className={styles.actionsCell}>
              <CheckIntersectionsButton scarId={info.row.original.id} />
              <RecalculateButton scarId={info.row.original.id} />
            </div>
          ),
        }),
      ] as ColumnDef<FoucsDiagnosisRecord>[],
    []
  );

  const renderSubRow = useCallback(
    (row: Row<FoucsDiagnosisRecord>) => (
      <DiagnosisSubRow scarId={row.original.id} />
    ),
    []
  );

  const getRowCanExpand = useCallback(() => true, []);

  const isAnalysisBusy =
    analysisStatus === 'running' || analysisStatus === 'classifying';

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        setSort(null);
      } else {
        setSort({
          column: sorting[0].id,
          direction: sorting[0].desc ? 'desc' : 'asc',
        });
      }
      setPage(1);
    },
    [setSort, setPage]
  );

  return (
    <>
      <PageHeader breadcrumbs={[]} title="FOUCS Diagnosis" actions={null} />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          <p>
            Each row is a subtract whose FOUCS analysis could not find a valid
            horizontal coverage set. The <strong>Failure Reason</strong> column
            categorizes why:
          </p>
          <ul className={styles.reasonList}>
            <li>
              <Tag type="purple" size="sm">
                Needs Analysis
              </Tag>{' '}
              Coverage analysis has not been run yet — click{' '}
              <strong>Run Coverage Analysis</strong> to classify.
            </li>
            <li>
              <Tag type="magenta" size="sm">
                No Eligible Horizontal Units
              </Tag>{' '}
              Horizontal units may intersect but none are LTD Capable, so
              eligible horizontal coverage is 0%.
            </li>
            <li>
              <Tag type="red" size="sm">
                Below Threshold
              </Tag>{' '}
              Horizontal units exist but their combined coverage is below the
              99% minimum.
            </li>
            <li>
              <Tag type="teal" size="sm">
                Overlap Conflicts
              </Tag>{' '}
              Coverage reaches 99%+ but overlapping units prevent a valid
              non-overlapping set.
            </li>
            <li>
              <Tag type="green" size="sm">
                Should Match
              </Tag>{' '}
              Coverage reaches 99%+ and no pairwise overlap exceeds the
              threshold — should produce a valid set after recalculation.
            </li>
          </ul>
          <p>
            Click <strong>Check Intersections</strong> to run a live spatial
            query and see the raw intersecting units, FOUCS results for both
            horizontal and vertical, and the total horizontal coverage
            available. Click <strong>Recalculate</strong> to enqueue a
            background job that re-runs the full coverage analysis for that
            subtract.
          </p>
        </div>
      </div>

      {isLoading && <InlineLoading description="Loading diagnosis data..." />}

      {error && <div className={styles.error}>Error: {error.message}</div>}

      {!isLoading && !error && (
        <>
          {stats && breakdown && (
            <StatsBar
              stats={[
                ...(breakdown.needsAnalysis > 0
                  ? [
                      {
                        label: 'Needs Analysis',
                        value: breakdown.needsAnalysis,
                        tagType: 'purple' as const,
                      },
                    ]
                  : []),
                {
                  label: 'No Eligible Horizontal Units',
                  value: breakdown.allFiltered,
                  tagType: 'magenta',
                },
                {
                  label: 'Overlap Conflicts',
                  value: breakdown.overlapConflicts,
                  tagType: 'teal',
                },
                {
                  label: 'Should Match',
                  value: breakdown.shouldMatch ?? 0,
                  tagType: 'green',
                },
                {
                  label: 'Below Threshold',
                  value: breakdown.belowThreshold,
                  tagType: 'red',
                },
                {
                  label: 'Total Analysis Results',
                  value: stats.totalAnalysisCount,
                  tagType: 'gray',
                },
              ]}
            />
          )}

          <div className={styles.coverageToolbar}>
            <Button
              kind="tertiary"
              size="md"
              disabled={isAnalysisBusy || runState.state === 'loading'}
              onClick={() => triggerRun()}
            >
              {isAnalysisBusy
                ? 'Running Coverage Analysis…'
                : 'Run Coverage Analysis'}
            </Button>
            {analysisStatus === 'running' && analysisProgress && (
              <span className={styles.analysisStatus}>
                {analysisProgress.processed} / {analysisProgress.total}{' '}
                subtracts analyzed…
              </span>
            )}
            {analysisStatus === 'classifying' && (
              <span className={styles.analysisStatus}>
                Classifying overlap conflicts…{' '}
                {classifyingProgress &&
                  `${classifyingProgress.processed} / ${classifyingProgress.total}`}
              </span>
            )}
            {analysisStatus === 'complete' && (
              <span className={styles.analysisStatus}>
                Complete — {analysisProgress?.total ?? 0} subtracts in{' '}
                {analysisElapsed}s
              </span>
            )}
            {analysisStatus === 'error' && (
              <span className={styles.error}>
                Coverage analysis failed. Check Sidekiq logs.
              </span>
            )}
            {runState.state === 'hasError' && (
              <span className={styles.error}>
                Failed to start:{' '}
                {runState.error instanceof Error
                  ? runState.error.message
                  : 'Unknown error'}
              </span>
            )}
          </div>

          <DataTable
            data={data}
            columns={allColumns}
            getRowId={(row) => String(row.id)}
            persistenceKey="foucs-diagnosis"
            renderRowSubComponent={renderSubRow}
            getRowCanExpand={getRowCanExpand}
            onSortChange={(s) => handleSortChange(s.sorting)}
            config={{
              sorting: true,
              globalSearch: true,
              columnFilters: false,
              columnReordering: false,
              columnPinning: false,
              columnVisibility: false,
              export: true,
            }}
          />

          {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={page}
              pageSize={pagination.pageSize}
              pageSizes={[100, 1000, 5000]}
              totalItems={pagination.totalRecords}
              onChange={({
                page: newPage,
                pageSize: newPageSize,
              }: {
                page: number;
                pageSize: number;
              }) => {
                setPage(newPage);
                setPageSize(newPageSize);
              }}
            />
          )}
        </>
      )}
    </>
  );
};
